import React from 'react';
// import { discount } from 'widok-common-public';
import { withLocalization } from "../locales";
import { Card, Theme, createStyles, makeStyles } from '@material-ui/core';
import { DiscountSearchErrorType, DiscountViewModel, useDiscountViewModel } from './hooks';
import { LoadingWrapperNew } from '../util/ui/Loading';
import { Localized, Money } from 'widok-bridge';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import QRCode from "react-qr-code";
import getLocalized from '../locales/Localized';
import { addMoney, formatMoney, formatPriceWithDefault, getMoneyCurrency } from '../util/FormatPrice';
import { AsyncError } from '../util/Loadable';
import { Alert } from '@mui/material';
import FixedLayout from '../util/ui/FixedLayout';
import { LocalDate } from '@js-joda/core';

export function Page() {
    return withLocalization(<PageBase />);
}

function PageBase() {
    const classes = useStyles();
    const discountViewModel = useDiscountViewModel();
    return (
        <FixedLayout>
            <div className={classes.root}>
                <div className={classes.content}>
                    <LoadingWrapperNew waitingFor={[discountViewModel]}
                        content={
                            discountViewModel.obj && (
                                <DiscountItemViewer viewModel={discountViewModel.obj} />
                            )}
                        fallbackContent={(errors: AsyncError[]) => (
                            <React.Fragment>
                                {errors.map((err, idx) => (
                                    <React.Fragment key={`search-error-${idx}`}>
                                        {(err.customId === DiscountSearchErrorType.SEARCH_NO_RESULTS) && (
                                            <Alert severity='error' style={{ width: '100%', }}>
                                                <FormattedMessage id={'discounts.viewer.search_failed'} />
                                            </Alert>)}
                                        {(err.customId === DiscountSearchErrorType.SEARCH_FAILED) && (
                                            <Alert severity='error' style={{ width: '100%', }}>
                                                <FormattedMessage id={'common.error.internal'} />
                                            </Alert>)}
                                        {(err.customId === DiscountSearchErrorType.NO_ID) && (
                                            <Alert severity='error' style={{ width: '100%', }}>
                                                <FormattedMessage id={'discounts.viewer.search_failed'} />
                                            </Alert>)}
                                    </React.Fragment>
                                ))}
                            </React.Fragment>
                        )} />
                </div>
            </div>
        </FixedLayout>
    )
}

function getItemLabel(label: Localized<string> | undefined, intl: IntlShape) {
    if (label === undefined) {
        return '?';
    }
    return getLocalized(label, intl.locale) ?? intl.formatMessage({ id: 'common.error' });
}

function DiscountItemViewer({ viewModel }: { viewModel: DiscountViewModel }) {
    const classes = useStyles();
    const item = viewModel.discountItem.obj;
    const usedAmounts: (Money | undefined)[] = viewModel.discountUsages.map((usage) => usage.obj.usedAmount);
    const usedDiscount: Money = addMoney(...usedAmounts);
    const intl = useIntl();
    const today = LocalDate.now();
    const idSubstring = item.id?.substring(0, 4).toUpperCase();
    return (
        <Card className={classes.mainCard}>

            {item.expirationDate?.isBefore(today) && (
                <Alert severity='error' style={{ width: '100%', }}>
                    <FormattedMessage id={'discounts.viewer.discount-expired'} />
                </Alert>)}
            <div className={classes.qrCodeWrapper}>
                <QRCode value={viewModel.qrCodeValue} size={128} className={classes.qrCode} />
                {idSubstring && (<div className={classes.qrCodeSubtext}>{idSubstring}</div>)}
            </div>
            {item.clientIdentifier?.label && (
                <p style={{ whiteSpace: "pre-wrap" }}>
                    <b>
                        {getItemLabel(item.clientIdentifier.label, intl)}
                    </b>
                </p>
            )}

            <p style={{ whiteSpace: "pre-wrap" }}>
                <>
                    <FormattedMessage
                        id={'discounts.view.summary'}
                        values={{
                            percentage: item.percentage ?? 0,
                            maximumAmount: formatMoney(item.maximumAmount),
                            expirationDate: item.expirationDate?.toString(),
                            usedDiscount: formatPriceWithDefault(usedDiscount, getMoneyCurrency(item.maximumAmount))
                        }} />
                </>
            </p>
        </Card>
    );
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            display: 'flex',
            flexDirection: 'column',
            alignContent: 'space-between',
            width: '100%',
            height: '100%',
            alignItems: 'center',
            padding: '10px 10px 0px 10px',
        },
        content: {
            maxWidth: '1024px',
            width: '100%',
            marginBottom: '16px',
        },
        mainCard: {
            textAlign: 'center',
        },
        qrCodeWrapper: {
            margin: '16px',
            padding: '16px',
            display: 'inline-block',
            border: '1px solid #ccc',
        },
        qrCode: {
            margin: '16px',
        },
        qrCodeSubtext: {
            fontSize: '24px',
            fontWeight: 'bold',
        },
    }));